"use client";

import { useMemo, useState, useTransition } from "react";
import { Loader2, ThumbsDown, ThumbsUp } from "lucide-react";

import { createAIChatFeedback } from "@/server-actions/ai-chat-feedback/actions";
import { cn } from "@/lib/utils";

export default function AIChatFeedbackButtons({
  question,
  answer,
  source = "GENERIC",
  contextId,
  contextType,
  workspaceId,
  messageId,
  metadata,
  responseTimeMs,
  trailingAction = null,
}) {
  const [selection, setSelection] = useState(null);
  const [error, setError] = useState(null);
  const [isPending, startTransition] = useTransition();

  const normalizedQuestion = useMemo(
    () => (typeof question === "string" ? question.trim() : ""),
    [question]
  );

  const normalizedAnswer = useMemo(
    () => (typeof answer === "string" ? answer.trim() : ""),
    [answer]
  );

  const canSubmit = Boolean(
    normalizedQuestion && normalizedAnswer && typeof source === "string"
  );

  const handleSubmit = (isPositive) => {
    if (!canSubmit || isPending || selection) return;

    startTransition(async () => {
      try {
        setError(null);
        const selected = isPositive ? "liked" : "disliked";
        const safeMetadata =
          metadata && typeof metadata === "object" ? metadata : {};
        await createAIChatFeedback({
          question: normalizedQuestion,
          answer: normalizedAnswer,
          isPositive,
          source,
          contextId,
          contextType,
          workspaceId,
          messageId,
          responseTimeMs,
          metadata: {
            ...safeMetadata,
            messageId: messageId || safeMetadata?.messageId,
            reaction: selected,
          },
        });
        setSelection(selected);
      } catch (submitError) {
        console.error("[AIChatFeedbackButtons] Failed to save feedback", submitError);
        setError("Couldn't save feedback. Please try again.");
      }
    });
  };

  return (
    <div className="flex flex-nowrap items-center gap-2 text-xs text-slate-500">
      <span className="whitespace-nowrap text-slate-500">Was this helpful?</span>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          aria-label="Thumbs up"
          disabled={!canSubmit || isPending || Boolean(selection)}
          onClick={() => handleSubmit(true)}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-full border text-slate-500 transition duration-150 motion-safe:hover:scale-110 hover:border-slate-300 hover:text-slate-800 disabled:hover:scale-100",
            selection === "liked"
              ? "border-green-500 bg-green-50 text-green-700"
              : "border-slate-200 bg-white"
          )}
        >
          {isPending && selection === "liked" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ThumbsUp className="h-4 w-4" />
          )}
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          disabled={!canSubmit || isPending || Boolean(selection)}
          onClick={() => handleSubmit(false)}
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-full border text-slate-500 transition duration-150 motion-safe:hover:scale-110 hover:border-slate-300 hover:text-slate-800 disabled:hover:scale-100",
            selection === "disliked"
              ? "border-warning-500 bg-warning-50 text-warning-700"
              : "border-slate-200 bg-white"
          )}
        >
          {isPending && selection === "disliked" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ThumbsDown className="h-4 w-4" />
          )}
        </button>
        {trailingAction}
      </div>
      {selection ? (
        <span className="text-slate-500">
          {selection === "liked" ? "Thanks for the thumbs up" : "Thanks for the feedback"}
        </span>
      ) : null}
      {error ? <span className="text-rose-600">{error}</span> : null}
    </div>
  );
}
